import { createFileRoute } from '@tanstack/react-router';
import {
  ActionIcon, Badge, Button, Card, Center, Checkbox, Group, Image, Loader, Modal, Paper,
  SegmentedControl, SimpleGrid, Stack, Text, TextInput, ThemeIcon, Tooltip,
} from '@mantine/core';
import { InlineLoader } from '@/shared/components/LoadingState';
import { Dropzone, IMAGE_MIME_TYPE, PDF_MIME_TYPE } from '@mantine/dropzone';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import {
  IconCheck, IconCopy, IconDownload, IconEye, IconFile, IconFileText, IconFolderOpen,
  IconPdf, IconPencil, IconPhoto, IconSearch, IconTrash, IconUpload, IconX,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { deleteOne, fetchAllPages, updateOne } from '@/shared/api/jsonapi/client';
import { formatBytes, MAX_UPLOAD_BYTES, uploadMediaFile, MEDIA_CAT } from '@/shared/lib/mediaUpload';
import { notifySuccess, notifyError } from '@/shared/lib/notify';
import { useDownloadMedia } from '@/shared/lib/useScaricaMedia';
import { notifications } from '@mantine/notifications';
import { EmptyState } from '@/shared/components/EmptyState';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { MobileMedia } from '@/features/media/mobile/MobileMedia';
import { fetchMediaDataUrl, mediaImageQueryKey } from '@/shared/lib/mediaImage';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import { env } from '@/shared/lib/env';

/** Campi reali del contratto BE (media-files): originalFileName/extension/fileUrl/
 *  primaryContentType/uploadDate/alt. `base64` (immagine intera, pesante) NON è
 *  richiesto nella lista — solo on-demand in anteprima. */
interface MediaFileRow extends Denormalized {
  type: 'media-files';
  id: string;
  originalFileName?: string;
  extension?: string;
  fileUrl?: string;
  primaryContentType?: string;
  uploadDate?: string;
  alt?: string;
  base64?: string;
}

// Lista SOLO metadata (niente base64: nel list-endpoint arriva "NoData" ed è comunque
// pesante). La miniatura reale la carica MediaThumb per-immagine via single-GET.
const LIST_FIELDS = ['originalFileName', 'extension', 'fileUrl', 'primaryContentType', 'uploadDate', 'alt'];

/** Taglia delle miniature in griglia: vedi il commento su MediaThumb per il perché di `md`. */
const THUMB_SIZE = 'md' as const;

export const Route = createFileRoute('/_app/media')({ component: MediaRoute });

/** Selettore: media library mobile (MaeDesign) vs griglia desktop completa. */
function MediaRoute() {
  const isMobile = useIsMobileShell();
  return isMobile ? <MobileMedia /> : <MediaPage />;
}

type Kind = 'all' | 'image' | 'pdf' | 'doc' | 'other';

function displayName(m: MediaFileRow): string {
  const base = m.originalFileName ?? m.id;
  if (m.extension && !base.toLowerCase().endsWith('.' + m.extension.toLowerCase())) return `${base}.${m.extension}`;
  return base;
}
function kindOf(m: MediaFileRow): Kind {
  const t = (m.primaryContentType ?? '').toLowerCase();
  const ext = (m.extension ?? '').toLowerCase();
  if (t.startsWith('image/') || /^(png|jpe?g|gif|webp|svg)$/.test(ext)) return 'image';
  if (t === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (/^(docx?|xlsx?|pptx?|txt|csv|md)$/.test(ext)) return 'doc';
  return 'other';
}
function iconFor(k: Kind): typeof IconFile {
  if (k === 'image') return IconPhoto;
  if (k === 'pdf') return IconPdf;
  if (k === 'doc') return IconFileText;
  return IconFile;
}

async function uploadOne(file: File): Promise<string> { return uploadMediaFile(file, MEDIA_CAT.toClassify); }

/**
 * Miniatura di un media. Carica il base64 via single-GET, ma SOLO quando la card entra
 * nel viewport (IntersectionObserver) → niente decine di richieste all'apertura. Per gli
 * altri tipi (o thumb assente) mostra l'icona.
 *
 * **Taglia `md`, non `sm`.** Misurato sul BE: `sm` è 100px sul lato lungo, mentre la card
 * disegna un riquadro alto 120px e largo quanto la colonna (~180px), quindi l'immagine
 * veniva ingrandita ~2× — 4× su schermo retina — e si vedeva sgranata. `md` è 854px sul
 * lato lungo: nitida anche a densità doppia. `lg` no: arriva a 2560px e 1,6 MB di base64
 * per una griglia di decine di card.
 */
function MediaThumb({ file }: { file: MediaFileRow }) {
  const k = kindOf(file);
  const Icon = iconFor(k);
  const color = k === 'image' ? 'brand' : k === 'pdf' ? 'red' : k === 'doc' ? 'blue' : 'gray';
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (k !== 'image' || inView) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setInView(true); io.disconnect(); }
    }, { rootMargin: '250px' });
    io.observe(el);
    return () => io.disconnect();
  }, [k, inView]);

  const thumbQ = useQuery({
    queryKey: mediaImageQueryKey(file.id, THUMB_SIZE),
    enabled: k === 'image' && inView,
    staleTime: 10 * 60_000,
    queryFn: () => fetchMediaDataUrl(file.id, THUMB_SIZE),
  });
  const src = thumbQ.data ?? undefined;
  const showImg = k === 'image' && Boolean(src);

  return (
    <div ref={ref} style={{ height: 120 }}>
      {showImg ? (
        <img src={src} alt={displayName(file)} loading="lazy" decoding="async" style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }} />
      ) : (
        <Center h={120} bg="var(--mantine-color-default-hover)">
          {k === 'image' && inView && thumbQ.isLoading
            ? <Loader size="sm" />
            : <ThemeIcon size={44} variant="light" color={color} radius="md"><Icon size={24} /></ThemeIcon>}
        </Center>
      )}
    </div>
  );
}

function MediaPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Kind>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 200);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<MediaFileRow | null>(null);
  const [renameTarget, setRenameTarget] = useState<MediaFileRow | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameOpened, renameCtrl] = useDisclosure(false);

  const listQ = useQuery({
    queryKey: ['media-files'],
    queryFn: () => fetchAllPages<MediaFileRow>('media-files', {
      sort: ['-uploadDate', '-id'],
      fields: { 'media-files': LIST_FIELDS }
    }, 200),
  });

  // Anteprima: carica il base64 (pesante) solo per il file selezionato.
  //
  // ⚠️ Era la causa di «Anteprima non disponibile per questo tipo di file» su file che
  // l'anteprima sa mostrare benissimo: con lo sparse fieldset
  // `fields[media-files]=base64,…` il BE risponde 200 ma con `base64` **stringa vuota**
  // (verificato sul dev), quindi il ramo immagine/pdf non scattava mai e si finiva nel
  // fallback. Senza `fields` il data-URL arriva completo; `size=lg` è la taglia piena.
  // `size=full` NON esiste: restituisce 500.
  const previewQ = useQuery({
    queryKey: mediaImageQueryKey(preview?.id, 'lg'),
    enabled: Boolean(preview),
    staleTime: 5 * 60_000,
    queryFn: () => fetchMediaDataUrl(preview!.id, 'lg'),
  });
  const previewSrc = previewQ.data ?? undefined;

  const items = useMemo(() => listQ.data ?? [], [listQ.data]);

  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return items.filter((m) => {
      if (filter !== 'all' && kindOf(m) !== filter) return false;
      if (q && !displayName(m).toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, filter, debouncedSearch]);

  const counts = useMemo(() => ({
    all: items.length,
    image: items.filter((m) => kindOf(m) === 'image').length,
    pdf: items.filter((m) => kindOf(m) === 'pdf').length,
    doc: items.filter((m) => kindOf(m) === 'doc').length,
    other: items.filter((m) => kindOf(m) === 'other').length,
  }), [items]);

  const upload = useMutation({
    mutationFn: async (files: File[]) => { for (const f of files) await uploadOne(f); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['media-files'] }); notifySuccess('Upload completato'); },
    onError: (err) => notifyError(err, 'Upload fallito'),
  });

  const remove = useMutation({
    mutationFn: (ids: string[]) => Promise.all(ids.map((id) => deleteOne('media-files', id))),
    onSuccess: (_, ids) => {
      qc.invalidateQueries({ queryKey: ['media-files'] });
      setSelected(new Set());
      notifySuccess(`${ids.length} file rimoss${ids.length === 1 ? 'o' : 'i'}`);
    },
    onError: (err) => notifyError(err, 'Rimozione fallita'),
  });

  const rename = useMutation({
    // Il campo reale è `originalFileName` (non `fileName`).
    mutationFn: () => updateOne('media-files', { id: renameTarget!.id, attributes: { originalFileName: renameValue.trim() } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['media-files'] });
      notifySuccess('File rinominato'); renameCtrl.close(); setRenameTarget(null);
    },
    onError: (err) => notifyError(err, 'Rinomina fallita'),
  });

  const toggleSelect = (id: string) => {
    setSelected((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };

  const askDeleteSelected = () => {
    if (selected.size === 0) return;
    modals.openConfirmModal({
      title: 'Eliminare file selezionati?',
      children: <Text size="sm">Stai per eliminare definitivamente <strong>{selected.size}</strong> file. L&apos;operazione non è reversibile.</Text>,
      labels: { confirm: 'Elimina', cancel: 'Annulla' }, confirmProps: { color: 'red' },
      onConfirm: () => remove.mutate(Array.from(selected)),
    });
  };
  const askDeleteSingle = (m: MediaFileRow) => {
    modals.openConfirmModal({
      title: 'Eliminare questo file?',
      children: <Text size="sm">&quot;{displayName(m)}&quot; verrà eliminato definitivamente.</Text>,
      labels: { confirm: 'Elimina', cancel: 'Annulla' }, confirmProps: { color: 'red' },
      onConfirm: () => remove.mutate([m.id]),
    });
  };
  // ⚠️ `…/binary` NON esiste sul BE (404 verificato sul dev: nessuna rotta `binary` nel
  // repo backend): copiava un link rotto. Si copia l'URL della risorsa JSON:API, che
  // risponde davvero e contiene il file in `attributes.base64`.
  /* I byte da `/download`, con l'`Authorization`: vedi `mediaDownload.ts`. */
  const { inProgress, download } = useDownloadMedia();
  const copyUrl = async (m: MediaFileRow) => {
    /* L'indirizzo dell'**API**, non quello della pagina: nell'app
       `window.location.origin` vale `https://localhost` e il link copiato non porta
       da nessuna parte. */
    const url = `${env.VITE_API_BASE_URL}/api/media-files/${m.id}`;
    try { await navigator.clipboard.writeText(url); notifications.show({ color: 'green', message: 'URL della risorsa copiato' }); }
    catch (err) { notifyError(err, 'Impossibile copiare'); }
  };
  const openRename = (m: MediaFileRow) => { setRenameTarget(m); setRenameValue(displayName(m)); renameCtrl.open(); };

  return (
    <MaeShell>
      <MaePageHead
        icon={<IconPhoto size={22} />}
        tone="azzurro"
        title="Media library"
        sub={`${items.length} file · immagini, PDF e documenti dell’azienda.`}
      />
      <Stack>
        {/*
          ⚠️ `inputProps` con l'etichetta: `Dropzone` rende un `<input type="file">` nascosto
          (`tabindex="-1"`, ritagliato) che axe segnala senza nome. Non e' pedanteria — quell'input
          **e'** il controllo di caricamento: chi usa un lettore di schermo lo trova nel modulo e
          non sa cosa accetti. Il testo dice anche il limite, che a schermo sta scritto sotto.
        */}
        <Dropzone
          inputProps={{ 'aria-label': 'Carica immagini, PDF o documenti' }}
          onDrop={(files) => void upload.mutate(files)}
          onReject={() => notifyError('File rifiutato')}
          maxSize={MAX_UPLOAD_BYTES}
          accept={[...IMAGE_MIME_TYPE, ...PDF_MIME_TYPE]}
          loading={upload.isPending}
          multiple
        >
          <Group justify="center" gap="xl" mih={120} style={{ pointerEvents: 'none' }}>
            <Dropzone.Accept><IconUpload size={48} color="var(--mantine-color-brand-6)" /></Dropzone.Accept>
            <Dropzone.Reject><IconX size={48} color="var(--mantine-color-red-6)" /></Dropzone.Reject>
            <Dropzone.Idle><IconUpload size={48} color="var(--mantine-color-dimmed)" /></Dropzone.Idle>
            <div>
              <Text size="lg">Trascina file o clicca per caricare</Text>
              <Text size="sm" c="dimmed" mt={4}>Immagini e PDF fino a {formatBytes(MAX_UPLOAD_BYTES)} · selezione multipla</Text>
            </div>
          </Group>
        </Dropzone>

        <Group justify="space-between" wrap="wrap">
          <Group gap="sm" wrap="wrap">
            <TextInput
              placeholder="Cerca per nome…"
              leftSection={<IconSearch size={14} />}
              rightSection={search ? (
                <ActionIcon variant="subtle" size="sm" aria-label="Cancella ricerca" onClick={() => setSearch('')}><IconX size={12} /></ActionIcon>
              ) : null}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              w={240}
            />
            <SegmentedControl
              value={filter}
              onChange={(v) => setFilter(v as Kind)}
              data={[
                { value: 'all', label: `Tutti (${counts.all})` },
                { value: 'image', label: `Immagini (${counts.image})` },
                { value: 'pdf', label: `PDF (${counts.pdf})` },
                { value: 'doc', label: `Doc (${counts.doc})` },
                { value: 'other', label: `Altro (${counts.other})` },
              ]}
              size="xs"
            />
          </Group>
          {selected.size > 0 && (
            <Group gap="xs">
              <Badge color="brand" variant="filled">{selected.size} selezionat{selected.size === 1 ? 'o' : 'i'}</Badge>
              <Button size="xs" variant="subtle" onClick={() => setSelected(new Set())}>Deseleziona</Button>
              <Button size="xs" color="red" variant="light" leftSection={<IconTrash size={14} />} onClick={askDeleteSelected} loading={remove.isPending}>
                Elimina selezionati
              </Button>
            </Group>
          )}
        </Group>

        {listQ.isError ? (
          <QueryErrorAlert error={listQ.error} onRetry={() => void listQ.refetch()} />
        ) : listQ.isLoading ? (
          <InlineLoader label="Caricamento della libreria media…" minHeight={240} />
        ) : filtered.length === 0 ? (
          <Paper withBorder radius="md">
            <EmptyState
              icon={IconFolderOpen}
              title={items.length === 0 ? 'Nessun file caricato' : 'Nessun file corrisponde ai filtri'}
              description={items.length === 0 ? 'Trascina file nel dropzone sopra per iniziare.' : 'Modifica filtri o cerca con altri termini.'}
            />
          </Paper>
        ) : (
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 6 }}>
            {filtered.map((m) => (
              <MediaCard
                key={m.id}
                file={m}
                selected={selected.has(m.id)}
                onToggleSelect={() => toggleSelect(m.id)}
                onPreview={() => setPreview(m)}
                onCopyUrl={() => void copyUrl(m)}
                onRename={() => openRename(m)}
                onDelete={() => askDeleteSingle(m)}
              />
            ))}
          </SimpleGrid>
        )}

        {/* Preview modal */}
        <Modal opened={preview != null} onClose={() => setPreview(null)} title={preview ? displayName(preview) : 'Anteprima'} size="xl" centered>
          {preview && (
            <Stack>
              {previewQ.isLoading ? (
                <Center py="xl"><Loader /></Center>
              ) : kindOf(preview) === 'image' && previewSrc ? (
                <Image src={previewSrc} alt={displayName(preview)} fit="contain" mah={600} />
              ) : kindOf(preview) === 'pdf' && previewSrc ? (
                <iframe src={previewSrc} title={displayName(preview)} style={{ width: '100%', height: 600, border: 'none', borderRadius: 8 }} />
              ) : (
                <Center py="xl">
                  <Stack align="center" gap={6}>
                    <ThemeIcon size={64} radius="xl" variant="light" color="gray"><IconFile size={32} /></ThemeIcon>
                    {/* Due cause diverse, due messaggi diversi: prima entrambe dicevano
                        "non disponibile per questo tipo di file", anche quando il tipo era
                        supportato e a mancare erano i dati. */}
                    {kindOf(preview) === 'image' || kindOf(preview) === 'pdf' ? (
                      <>
                        <Text size="sm" c="dimmed">Il contenuto del file non è arrivato dal sistema.</Text>
                        <Button size="xs" variant="light" loading={previewQ.isFetching} onClick={() => void previewQ.refetch()}>
                          Riprova
                        </Button>
                      </>
                    ) : (
                      <Text size="sm" c="dimmed">
                        Anteprima non disponibile per i file {preview.extension?.toUpperCase() || 'di questo tipo'}: scaricalo per aprirlo.
                      </Text>
                    )}
                  </Stack>
                </Center>
              )}
              <Group justify="space-between">
                <Stack gap={0}>
                  <Text size="sm" fw={500}>{displayName(preview)}</Text>
                  <Text size="xs" c="dimmed">
                    {preview.primaryContentType ?? preview.extension ?? 'sconosciuto'}
                    {preview.uploadDate && ` · ${dayjs(preview.uploadDate).format('DD/MM/YYYY HH:mm')}`}
                  </Text>
                </Stack>
                <Group gap="xs">
                  {/*
                    Il download **non** passa dall'anteprima. Era un `<a download>` su un
                    data-URL: dentro l'app non produce nessun file (e nessun errore), e sul web
                    salvava il base64 che il single-GET porta per **mostrare** il file — un
                    terzo piu' pesante dei byte, e per le immagini la taglia ridotta invece
                    dell'originale. Ora i byte arrivano da `/download`.
                  */}
                  <Button
                    variant="light"
                    leftSection={<IconDownload size={14} />}
                    size="sm"
                    onClick={() => void download(preview.id, displayName(preview))}
                    loading={inProgress === preview.id}
                  >
                    Scarica
                  </Button>
                  <Button variant="subtle" leftSection={<IconCopy size={14} />} size="sm" onClick={() => void copyUrl(preview)}>Copia URL</Button>
                </Group>
              </Group>
            </Stack>
          )}
        </Modal>

        {/* Rename modal */}
        <Modal opened={renameOpened} onClose={() => { renameCtrl.close(); setRenameTarget(null); }} title="Rinomina file" centered>
          <Stack>
            <TextInput label="Nome file" value={renameValue} onChange={(e) => setRenameValue(e.currentTarget.value)} data-autofocus />
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => { renameCtrl.close(); setRenameTarget(null); }}>Annulla</Button>
              <Button leftSection={<IconCheck size={14} />} disabled={!renameValue.trim()} loading={rename.isPending} onClick={() => rename.mutate()}>Salva</Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </MaeShell>
  );
}

function MediaCard({
  file, selected, onToggleSelect, onPreview, onCopyUrl, onRename, onDelete,
}: {
  file: MediaFileRow;
  selected: boolean;
  onToggleSelect: () => void;
  onPreview: () => void;
  onCopyUrl: () => void;
  onRename: () => void;
  onDelete: () => void;
}) {
  return (
    <Card withBorder padding="xs" radius="md" style={{ outline: selected ? '2px solid var(--mantine-color-brand-filled)' : undefined, outlineOffset: -2 }}>
      <Card.Section style={{ position: 'relative', cursor: 'pointer' }} onClick={onPreview}>
        <MediaThumb file={file} />
        <Checkbox size="xs" aria-label={`Seleziona ${displayName(file)}`} checked={selected} onChange={onToggleSelect} onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: 6, left: 6 }} />
      </Card.Section>
      <Tooltip label={displayName(file)}>
        <Text size="xs" mt="xs" lineClamp={1}>{displayName(file)}</Text>
      </Tooltip>
      <Text size="xs" c="dimmed">{file.uploadDate ? dayjs(file.uploadDate).format('DD/MM/YYYY') : (file.extension ?? '—')}</Text>
      <Group gap={2} mt={4} justify="flex-end">
        <Tooltip label="Anteprima"><ActionIcon size="sm" variant="subtle" aria-label="Anteprima" onClick={onPreview}><IconEye size={12} /></ActionIcon></Tooltip>
        <Tooltip label="Copia URL"><ActionIcon size="sm" variant="subtle" aria-label="Copia URL" onClick={onCopyUrl}><IconCopy size={12} /></ActionIcon></Tooltip>
        <Tooltip label="Rinomina"><ActionIcon size="sm" variant="subtle" aria-label="Rinomina" onClick={onRename}><IconPencil size={12} /></ActionIcon></Tooltip>
        <Tooltip label="Elimina"><ActionIcon size="sm" variant="subtle" color="red" aria-label="Elimina file" onClick={onDelete}><IconTrash size={12} /></ActionIcon></Tooltip>
      </Group>
    </Card>
  );
}
